import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Loader2, RefreshCw, Search, X } from "lucide-react";
import { GetChatRoomsAPI, LeaveChatRoomAPI } from "@/services/api/chat/getChatRooms";
import socket from "@/services/config/socket.io/socket.io";
import { registerPresence } from "@/services/middleware/chatHelper";
import type { ChatRoom } from "@/utils/chatFormat";
import { getRoomTitle, sortRoomsByLatestMessage } from "@/utils/chatFormat";
import ChatRoomRow from "./ChatRoomRow";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import Modal from "@/components/Modal";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

type PresenceUpdate = {
  user_id?: string;
  is_online?: boolean;
  last_seen?: number | string | null;
};

type ChatRoomListProps = {
  /** `users.id` of the signed-in account (business logins use `business.user_id`). */
  currentUserId: string;
  /** Route prefix the room rows navigate to, e.g. `/student/chat`. */
  basePath: string;
  title: string;
  /** Shown when there are no rooms at all (the "no search match" copy is shared). */
  emptyMessage: string;
  className?: string;
  /** Hides the header refresh button in favour of a pull-to-refresh gesture. */
  hideRefresh?: boolean;
};

/**
 * Chat room list shared by the student and business portals — the web port of
 * the RN `ChatScreen`. Rooms come from the REST endpoint and are kept live over
 * the socket: the list is re-fetched when the backend pushes `gotChatRooms`,
 * when any joined room receives a message (each room is its own socket event)
 * and when a participant's `presence_update` arrives.
 */
const ChatRoomList: React.FC<ChatRoomListProps> = ({
  currentUserId,
  basePath,
  title,
  emptyMessage,
  className = "mx-auto max-w-3xl px-4 py-6",
  hideRefresh = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language = useSelector(
    (state: any) => state.setting?.language ?? "zh_hant",
  );
  const loggedIn = useSelector((state: any) => state.auth?.loggedIn);

  const [roomList, setRoomList] = useState<ChatRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [leaveTarget, setLeaveTarget] = useState<ChatRoom | null>(null);
  const [leaving, setLeaving] = useState(false);

  const fetchRooms = useCallback(async () => {
    if (!loggedIn || !currentUserId) return;
    const resp = await GetChatRoomsAPI();
    if (resp?.status === 0 && Array.isArray(resp.result)) {
      setRoomList(resp.result as ChatRoom[]);
    }
    setLoading(false);
  }, [loggedIn, currentUserId]);

  useEffect(() => {
    if (!loggedIn || !currentUserId) return;
    registerPresence(currentUserId);
    socket.connect();
    const onConnect = () => {
      socket.emit("getChatRooms", { user_id: currentUserId });
    };
    const onGotChatRooms = (rooms: unknown) => {
      if (Array.isArray(rooms)) {
        setRoomList(rooms as ChatRoom[]);
        setLoading(false);
      }
    };
    socket.on("connect", onConnect);
    socket.on("gotChatRooms", onGotChatRooms);
    fetchRooms();
    return () => {
      socket.off("connect", onConnect);
      socket.off("gotChatRooms", onGotChatRooms);
      socket.disconnect();
    };
  }, [loggedIn, currentUserId, fetchRooms]);

  useEffect(() => {
    const cleanups: Array<() => void> = [];
    roomList.forEach((room) => {
      if (!room?.id) return;
      const onRoomMessage = () => {
        fetchRooms();
      };
      socket.on(room.id, onRoomMessage);
      cleanups.push(() => socket.off(room.id, onRoomMessage));
    });
    return () => {
      cleanups.forEach((off) => off());
    };
  }, [roomList, fetchRooms]);

  useEffect(() => {
    const onPresenceUpdate = (data: PresenceUpdate) => {
      if (!data?.user_id) return;
      setRoomList((prev) =>
        prev.map((room) => ({
          ...room,
          participants: (room?.participants ?? []).map((participant) =>
            participant.user_id === data.user_id
              ? {
                  ...participant,
                  is_online: !!data.is_online,
                  last_seen: data.last_seen ?? null,
                }
              : participant,
          ),
        })),
      );
    };
    socket.on("presence_update", onPresenceUpdate);
    return () => {
      socket.off("presence_update", onPresenceUpdate);
    };
  }, []);

  const filteredRooms = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const matched = query
      ? roomList.filter((room) =>
          getRoomTitle(room, currentUserId).toLowerCase().includes(query),
        )
      : roomList;
    return sortRoomsByLatestMessage(matched);
  }, [roomList, searchQuery, currentUserId]);

  const handlePressRoom = (room: ChatRoom) => {
    navigate(`${basePath}/${room.id}`);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRooms();
    setRefreshing(false);
  };

  const handleLeave = async () => {
    if (!leaveTarget?.id || leaving) return;
    setLeaving(true);
    const resp = await LeaveChatRoomAPI({
      chat_room_id: leaveTarget.id,
      user_id: currentUserId,
    });
    setLeaving(false);
    setLeaveTarget(null);
    if (resp?.status === 0) {
      setRoomList((prev) => prev.filter((room) => room.id !== leaveTarget.id));
    }
  };

  const { refreshing: pullRefreshing, touchProps } = usePullToRefresh(
    handleRefresh,
  );

  return (
    <div className={className} {...(hideRefresh ? touchProps : {})}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {!hideRefresh && (
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:opacity-60"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            {t("Refresh")}
          </button>
        )}
      </div>

      <div className="mb-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
        <Search size={16} className="shrink-0 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t("Search chats...")}
          className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400"
        />
        {searchQuery.length > 0 && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            aria-label={t("Clear")}
            className="shrink-0 text-gray-400 transition hover:text-gray-600"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {hideRefresh && pullRefreshing && (
        <div className="mb-3 flex justify-center">
          <Loader2 size={18} className="animate-spin text-[#ffa217]" />
        </div>
      )}

      {loading ? (
        <AdminListSkeleton count={5} />
      ) : filteredRooms.length === 0 ? (
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="MaterialCommunityIcons"
            iconName="chat-outline"
            title={
              roomList.length > 0
                ? t("No chats match your search")
                : t("No chats yet")
            }
            message={
              roomList.length > 0 ? t("Try a different name.") : emptyMessage
            }
            actionLabel={t("Refresh")}
            onAction={handleRefresh}
          />
        </div>
      ) : (
        <div className="space-y-2.5">
          {filteredRooms.map((room) => (
            <ChatRoomRow
              key={room.id}
              room={room}
              currentUserId={currentUserId}
              language={language}
              onPress={handlePressRoom}
              onManage={setLeaveTarget}
            />
          ))}
        </div>
      )}

      <Modal
        visible={Boolean(leaveTarget)}
        onClose={() => setLeaveTarget(null)}
      >
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <p className="mb-5 text-base font-bold text-gray-900">
            {t("Leave this chat?")}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setLeaveTarget(null)}
              className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={handleLeave}
              disabled={leaving}
              className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-bold text-white transition hover:bg-red-600 disabled:opacity-60"
            >
              {leaving ? (
                <Loader2 size={16} className="mx-auto animate-spin" />
              ) : (
                t("Leave")
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChatRoomList;
